import type { IPhotoEditResult, IPhotoEditInitial } from './photo-editor.component';

type Crop = NonNullable<IPhotoEditInitial['crop']>;

/** Positions + scales an `<img>` inside an `overflow-hidden` tile so exactly
 *  the given crop rect fills the tile — the standard CSS crop-preview
 *  technique, for surfaces that store the crop as metadata alongside an
 *  uncropped image rather than baking it into pixels. Bind to the `<img>`
 *  via `[ngStyle]`; the tile itself needs `position: relative` and
 *  `overflow: hidden`. */
export function cropPreviewStyle(crop: Crop | undefined): Record<string, string> {
  const c = crop ?? { left: 0, top: 0, right: 1, bottom: 1 };
  const w = Math.max(c.right - c.left, 0.01);
  const h = Math.max(c.bottom - c.top, 0.01);
  return {
    position: 'absolute',
    left: `${-(c.left / w) * 100}%`,
    top: `${-(c.top / h) * 100}%`,
    width: `${100 / w}%`,
    height: `${100 / h}%`,
    'max-width': 'none',
  };
}

/**
 * Bakes a `PhotoEditorComponent` crop rectangle into actual pixels, for
 * surfaces with no backend photo entity to store `cropLeft/Top/Right/Bottom`
 * metadata on (unlike listing/site photos, which upload the full image and
 * let the backend crop on display — see `HelperFunctions.getPhotoUrl*()`).
 * `PhotoEditorComponent` itself only bakes in rotation + downscale, not the
 * crop, so this is the one remaining step to get a final cropped image.
 */
export function cropPhotoEditResultToBlob(result: IPhotoEditResult, quality = 0.85): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const { left, top, right, bottom } = result.crop;
      const srcX = Math.round(left * img.naturalWidth);
      const srcY = Math.round(top * img.naturalHeight);
      const srcW = Math.max(1, Math.round((right - left) * img.naturalWidth));
      const srcH = Math.max(1, Math.round((bottom - top) * img.naturalHeight));

      const canvas = document.createElement('canvas');
      canvas.width = srcW;
      canvas.height = srcH;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('2D canvas context unavailable')); return; }
      ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);

      canvas.toBlob(
        blob => (blob ? resolve(blob) : reject(new Error('Failed to encode cropped image'))),
        'image/jpeg',
        quality,
      );
    };
    img.onerror = () => reject(new Error('Failed to load image for cropping'));
    img.src = result.dataUrl;
  });
}

/** Same as `cropPhotoEditResultToBlob`, but returns a data URL — convenient
 *  for surfaces (like the RV-photos modal) that only ever store a string
 *  preview, never a `File`/`Blob` upload. */
export async function cropPhotoEditResultToDataUrl(result: IPhotoEditResult, quality = 0.85): Promise<string> {
  const blob = await cropPhotoEditResultToBlob(result, quality);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read cropped blob'));
    reader.readAsDataURL(blob);
  });
}
